import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { onboardingAPI } from '../../services/api';

function OnboardingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [onboarding, setOnboarding] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // School info editing
  const [editingSchool, setEditingSchool] = useState(false);
  const [schoolData, setSchoolData] = useState({});
  
  // Teacher upload
  const [showTeacherUpload, setShowTeacherUpload] = useState(false);
  const [teacherFile, setTeacherFile] = useState(null);
  const [teacherPreview, setTeacherPreview] = useState([]);
  const [uploadingTeachers, setUploadingTeachers] = useState(false);
  
  // Student upload
  const [showStudentUpload, setShowStudentUpload] = useState(false);
  const [studentFile, setStudentFile] = useState(null);
  const [studentPreview, setStudentPreview] = useState([]);
  const [uploadingStudents, setUploadingStudents] = useState(false);

  useEffect(() => {
    fetchOnboardingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchOnboardingData = async () => {
    setLoading(true);
    try {
      const response = await onboardingAPI.getById(id);
      setOnboarding(response.data);
      setTeachers(response.data.teachers || []);
      setStudents(response.data.students || []);
      setSchoolData({
        city: response.data.city,
        studentCount: response.data.studentCount,
        board: response.data.board,
        schoolEmail: response.data.schoolEmail,
        onboardingStatus: response.data.onboardingStatus
      });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch onboarding data');
    } finally {
      setLoading(false);
    }
  };

  const updateSchoolInfo = async () => {
    try {
      await onboardingAPI.update(id, schoolData);
      setSuccess('School information updated successfully!');
      setEditingSchool(false);
      await fetchOnboardingData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update school information');
    }
  };

  // Teacher upload handlers
  const handleTeacherFileChange = (e) => {
    setTeacherFile(e.target.files[0]);
  };

  const uploadTeacherFile = async () => {
    if (!teacherFile) {
      setError('Please select a file');
      return;
    }

    setUploadingTeachers(true);
    try {
      const response = await onboardingAPI.uploadTeachers(id, teacherFile);
      setTeacherPreview(response.data.teachers || []);
      setSuccess(`${response.data.count} teachers parsed successfully! Review and save.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload teacher file');
    } finally {
      setUploadingTeachers(false);
    }
  };

  const saveTeachers = async () => {
    try {
      await onboardingAPI.saveTeachers(id, teacherPreview);
      setSuccess('Teachers saved successfully!');
      setShowTeacherUpload(false);
      setTeacherFile(null);
      setTeacherPreview([]);
      await fetchOnboardingData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save teachers');
    }
  };

  // Student upload handlers
  const handleStudentFileChange = (e) => {
    setStudentFile(e.target.files[0]);
  };

  const uploadStudentFile = async () => {
    if (!studentFile) {
      setError('Please select a file');
      return;
    }

    setUploadingStudents(true);
    try {
      const response = await onboardingAPI.uploadStudents(id, studentFile);
      setStudentPreview(response.data.students || []);
      setSuccess(`${response.data.count} students parsed successfully! Review and save.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload student file');
    } finally {
      setUploadingStudents(false);
    }
  };

  const saveStudents = async () => {
    try {
      await onboardingAPI.saveStudents(id, studentPreview);
      setSuccess('Students saved successfully!');
      setShowStudentUpload(false);
      setStudentFile(null);
      setStudentPreview([]);
      await fetchOnboardingData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save students');
    }
  };

  const deleteTeacher = async (teacherId) => {
    if (!window.confirm('Are you sure you want to delete this teacher?')) return;
    
    try {
      await onboardingAPI.deleteTeacher(id, teacherId);
      setSuccess('Teacher deleted successfully!');
      await fetchOnboardingData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete teacher');
    }
  };

  const deleteStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;
    
    try {
      await onboardingAPI.deleteStudent(id, studentId);
      setSuccess('Student deleted successfully!');
      await fetchOnboardingData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete student');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(''), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6 flex items-center justify-center">
        <div className="inline-flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <span className="text-gray-600 font-medium text-lg">Loading onboarding data...</span>
        </div>
      </div>
    );
  }

  if (!onboarding) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Onboarding Not Found</h2>
          <button
            onClick={() => navigate('/admin/onboarding')}
            className="bg-gradient-to-r from-green-500 to-teal-600 text-white px-6 py-2 rounded-xl hover:from-green-600 hover:to-teal-700 transition-all duration-200 font-medium shadow-lg"
          >
            Back to Onboarding List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-3 sm:p-4 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-green-600 bg-clip-text text-transparent">
                  {onboarding.schoolName}
                </h1>
                <p className="text-sm text-gray-600">School Code: {onboarding.schoolCode}</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/onboarding')}
              className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-4 py-2 rounded-xl hover:from-gray-600 hover:to-gray-700 transition-all duration-200 font-medium shadow-lg"
            >
              Back to List
            </button>
          </div>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center space-x-2">
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center space-x-2">
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{success}</span>
            </div>
          )}
        </div>

        {/* School Information */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">School Information</h2>
            <button
              onClick={() => setEditingSchool(!editingSchool)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 text-sm font-medium"
            >
              {editingSchool ? 'Cancel' : 'Edit'}
            </button>
          </div>

          {editingSchool ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <input
                  type="text"
                  value={schoolData.city}
                  onChange={(e) => setSchoolData({...schoolData, city: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Student Count</label>
                <input
                  type="number"
                  value={schoolData.studentCount}
                  onChange={(e) => setSchoolData({...schoolData, studentCount: parseInt(e.target.value)})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Board</label>
                <select
                  value={schoolData.board}
                  onChange={(e) => setSchoolData({...schoolData, board: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="CBSE">CBSE</option>
                  <option value="ICSE">ICSE</option>
                  <option value="State Board">State Board</option>
                  <option value="IB">IB</option>
                  <option value="IGCSE">IGCSE</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">School Email</label>
                <input
                  type="email"
                  value={schoolData.schoolEmail}
                  onChange={(e) => setSchoolData({...schoolData, schoolEmail: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Onboarding Status</label>
                <select
                  value={schoolData.onboardingStatus}
                  onChange={(e) => setSchoolData({...schoolData, onboardingStatus: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="Pending">Pending</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={updateSchoolInfo}
                  className="w-full bg-gradient-to-r from-green-500 to-teal-600 text-white px-4 py-3 rounded-lg hover:from-green-600 hover:to-teal-700 transition-all duration-200 font-medium"
                >
                  Save Changes
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">City</p>
                <p className="text-base font-medium text-gray-900">{onboarding.city}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Student Count</p>
                <p className="text-base font-medium text-gray-900">{onboarding.studentCount}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Board</p>
                <p className="text-base font-medium text-gray-900">{onboarding.board}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">School Email</p>
                <p className="text-base font-medium text-gray-900">{onboarding.schoolEmail}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Onboarding Status</p>
                <p className="text-base font-medium text-gray-900">{onboarding.onboardingStatus}</p>
              </div>
            </div>
          )}
        </div>

        {/* Teachers Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">Teachers ({teachers.length})</h2>
            <button
              onClick={() => setShowTeacherUpload(!showTeacherUpload)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 text-sm font-medium"
            >
              {showTeacherUpload ? 'Cancel Upload' : 'Upload Excel'}
            </button>
          </div>

          {showTeacherUpload && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-medium text-gray-900 mb-2">Upload Teacher Excel</h3>
              <p className="text-sm text-gray-600 mb-3">
                Expected columns: First Name, Last Name, Class, Subject, Subject Specific Area, Section, Email, Phone Number
              </p>
              <div className="flex gap-2">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleTeacherFileChange}
                  className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
                />
                <button
                  onClick={uploadTeacherFile}
                  disabled={!teacherFile || uploadingTeachers}
                  className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-all duration-200 text-sm font-medium disabled:opacity-50"
                >
                  {uploadingTeachers ? 'Processing...' : 'Parse File'}
                </button>
              </div>

              {teacherPreview.length > 0 && (
                <div className="mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-gray-900">Preview ({teacherPreview.length} teachers)</h4>
                    <button
                      onClick={saveTeachers}
                      className="bg-gradient-to-r from-green-500 to-teal-600 text-white px-4 py-2 rounded-lg hover:from-green-600 hover:to-teal-700 transition-all duration-200 text-sm font-medium"
                    >
                      Save All Teachers
                    </button>
                  </div>
                  <div className="overflow-x-auto max-h-96">
                    <table className="min-w-full bg-white border border-gray-200 rounded-lg text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Class</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Section</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Subject</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Username</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Password</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {teacherPreview.map((teacher, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2">{teacher.firstName} {teacher.lastName}</td>
                            <td className="px-3 py-2">{teacher.class}</td>
                            <td className="px-3 py-2">{teacher.section}</td>
                            <td className="px-3 py-2">{teacher.subject}</td>
                            <td className="px-3 py-2">{teacher.email}</td>
                            <td className="px-3 py-2 font-mono text-xs">{teacher.username}</td>
                            <td className="px-3 py-2 font-mono text-xs">{teacher.plainPassword}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {teachers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No teachers added yet. Upload an Excel file to add teachers.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Class</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Section</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Subject</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Username</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Password</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {teachers.map((teacher) => (
                    <tr key={teacher._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{teacher.firstName} {teacher.lastName}</td>
                      <td className="px-4 py-3 text-sm">{teacher.class}</td>
                      <td className="px-4 py-3 text-sm">{teacher.section}</td>
                      <td className="px-4 py-3 text-sm">{teacher.subject}{teacher.subjectSpecificArea && ` (${teacher.subjectSpecificArea})`}</td>
                      <td className="px-4 py-3 text-sm">{teacher.email}</td>
                      <td className="px-4 py-3 text-sm">{teacher.phoneNumber}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-xs">{teacher.username}</span>
                          <button
                            onClick={() => copyToClipboard(teacher.username)}
                            className="text-blue-500 hover:text-blue-700"
                            title="Copy username"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-xs">{teacher.plainPassword}</span>
                          <button
                            onClick={() => copyToClipboard(teacher.plainPassword)}
                            className="text-blue-500 hover:text-blue-700"
                            title="Copy password"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => deleteTeacher(teacher._id)}
                          className="text-red-600 hover:text-red-800 text-xs px-2 py-1 bg-red-100 rounded"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Students Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">Students ({students.length})</h2>
            <button
              onClick={() => setShowStudentUpload(!showStudentUpload)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 text-sm font-medium"
            >
              {showStudentUpload ? 'Cancel Upload' : 'Upload Excel'}
            </button>
          </div>

          {showStudentUpload && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-medium text-gray-900 mb-2">Upload Student Excel</h3>
              <p className="text-sm text-gray-600 mb-3">
                Expected columns: First Name, Last Name, Roll No, Class, Section
              </p>
              <div className="flex gap-2">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleStudentFileChange}
                  className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
                />
                <button
                  onClick={uploadStudentFile}
                  disabled={!studentFile || uploadingStudents}
                  className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-all duration-200 text-sm font-medium disabled:opacity-50"
                >
                  {uploadingStudents ? 'Processing...' : 'Parse File'}
                </button>
              </div>

              {studentPreview.length > 0 && (
                <div className="mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-gray-900">Preview ({studentPreview.length} students)</h4>
                    <button
                      onClick={saveStudents}
                      className="bg-gradient-to-r from-green-500 to-teal-600 text-white px-4 py-2 rounded-lg hover:from-green-600 hover:to-teal-700 transition-all duration-200 text-sm font-medium"
                    >
                      Save All Students
                    </button>
                  </div>
                  <div className="overflow-x-auto max-h-96">
                    <table className="min-w-full bg-white border border-gray-200 rounded-lg text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Roll No</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Class</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Section</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Username</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Password</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {studentPreview.map((student, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2">{student.firstName} {student.lastName}</td>
                            <td className="px-3 py-2">{student.rollNo}</td>
                            <td className="px-3 py-2">{student.class}</td>
                            <td className="px-3 py-2">{student.section}</td>
                            <td className="px-3 py-2 font-mono text-xs">{student.username}</td>
                            <td className="px-3 py-2 font-mono text-xs">{student.plainPassword}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {students.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No students added yet. Upload an Excel file to add students.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Roll No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Class</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Section</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Username</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Password</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {students.map((student) => (
                    <tr key={student._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{student.firstName} {student.lastName}</td>
                      <td className="px-4 py-3 text-sm">{student.rollNo}</td>
                      <td className="px-4 py-3 text-sm">{student.class}</td>
                      <td className="px-4 py-3 text-sm">{student.section}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-xs">{student.username}</span>
                          <button
                            onClick={() => copyToClipboard(student.username)}
                            className="text-blue-500 hover:text-blue-700"
                            title="Copy username"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-xs">{student.plainPassword}</span>
                          <button
                            onClick={() => copyToClipboard(student.plainPassword)}
                            className="text-blue-500 hover:text-blue-700"
                            title="Copy password"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => deleteStudent(student._id)}
                          className="text-red-600 hover:text-red-800 text-xs px-2 py-1 bg-red-100 rounded"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OnboardingDetail;
